import { apiClient } from '../axios.config';
import { API_ENDPOINTS } from '../constants';
import type { ApiResponse } from '../types/common';
import type {
  StartChatRequest,
  StartChatResponse,
  SendMessageRequest,
  SendMessageResponse,
  GetChatParams,
  GetChatResponse,
  GetUserChatsResponse,
} from '../types/chat.types';

/**
 * Chat Service - All chat-related API calls
 */
export const chatService = {
  /**
   * Start a new chat with a character
   * Returns existing chat if one already exists
   * @param request - Character ID to start chat with
   * @returns Chat ID and character name, with initial message if new chat
   */
  startChat: async (request: StartChatRequest): Promise<ApiResponse<StartChatResponse>> => {
    const response = await apiClient.post<ApiResponse<StartChatResponse>>(
      API_ENDPOINTS.CHAT.START,
      request
    );
    return response.data;
  },

  /**
   * Send a message to the chat and get AI response
   * Consumes 1 message credit per request
   * @param request - Chat ID and message content
   * @returns User message, AI response, and remaining credits
   */
  sendMessage: async (request: SendMessageRequest): Promise<ApiResponse<SendMessageResponse>> => {
    const response = await apiClient.post<ApiResponse<SendMessageResponse>>(
      API_ENDPOINTS.CHAT.SEND_MESSAGE,
      request
    );
    return response.data;
  },

  /**
   * Get a specific chat with all messages and character details
   * @param params - Chat ID to retrieve
   * @returns Complete chat data with messages and character info
   */
  getChat: async (params: GetChatParams): Promise<ApiResponse<GetChatResponse>> => {
    const response = await apiClient.get<ApiResponse<GetChatResponse>>(
      API_ENDPOINTS.CHAT.GET_CHAT,
      { params }
    );
    return response.data;
  },

  /**
   * Get all chats for the authenticated user
   * Returns chat list with character info and last message preview
   * @returns Array of user chats with metadata
   */
  getUserChats: async (): Promise<ApiResponse<GetUserChatsResponse>> => {
    const response = await apiClient.get<ApiResponse<GetUserChatsResponse>>(
      API_ENDPOINTS.CHAT.GET_USER_CHATS
    );
    return response.data;
  },
};
